import { useState } from 'react';
import { Pencil, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { plzWarnung } from '@/lib/adresse';
import type { DetailLieferadresse, LieferadressePatch } from '@/types/detail';
import { FELD_KLASSE } from '@/lib/feldKlassen';

type Props = {
  bestellungId: number;
  adresse: DetailLieferadresse;
  onSave: (patch: LieferadressePatch) => void;
};

const inputClass = FELD_KLASSE;

export function LieferadresseEditor({ bestellungId, adresse, onSave }: Props) {
  const [open, setOpen] = useState(false);
  const [a, setA] = useState({
    anrede: adresse.anrede ?? '',
    name: adresse.name ?? '',
    strasse: adresse.strasse ?? '',
    plz: adresse.plz ?? '',
    ort: adresse.ort ?? '',
    land: adresse.land ?? 'Deutschland'
  });
  const [sync, setSync] = useState(false);

  // Beim Oeffnen den Stand frisch aus den Props ziehen und die
  // Stammdaten-Uebernahme wieder abwaehlen. Die Komponente bleibt im
  // geschlossenen Zustand gemountet — ohne diesen Reset waere die Checkbox
  // beim naechsten Oeffnen noch von der letzten Nutzung gesetzt (B29).
  function oeffnen(): void {
    setA({
      anrede: adresse.anrede ?? '',
      name: adresse.name ?? '',
      strasse: adresse.strasse ?? '',
      plz: adresse.plz ?? '',
      ort: adresse.ort ?? '',
      land: adresse.land ?? 'Deutschland'
    });
    setSync(false);
    setOpen(true);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={oeffnen}
        className="inline-flex min-h-[44px] items-center gap-1.5 text-xs text-ink-soft hover:text-akzent"
      >
        <Pencil className="h-3 w-3" />
        Lieferadresse ändern
      </button>
    );
  }

  // Weiche Warnungen: sie halten das Speichern nicht auf. Hart geprueft wird
  // erst beim Anlegen einer Zustell-Sendung (B28).
  const warnungen: string[] = [];
  const plzHinweis = plzWarnung(a.plz, a.land);
  if (plzHinweis !== null) warnungen.push(plzHinweis);
  if (a.strasse.trim() === '') warnungen.push('Straße ist leer.');
  if (a.ort.trim() === '') warnungen.push('Ort ist leer.');

  function speichern(): void {
    onSave({
      id: bestellungId,
      lieferadresse_anrede: a.anrede.trim() === '' ? null : a.anrede.trim(),
      lieferadresse_name: a.name.trim() === '' ? null : a.name.trim(),
      lieferadresse_strasse: a.strasse.trim() === '' ? null : a.strasse.trim(),
      lieferadresse_plz: a.plz.trim() === '' ? null : a.plz.trim(),
      lieferadresse_ort: a.ort.trim() === '' ? null : a.ort.trim(),
      lieferadresse_land: a.land.trim() === '' ? null : a.land.trim(),
      sync_stammdaten: sync
    });
    setOpen(false);
  }

  return (
    <div className="space-y-2 rounded-md border border-line bg-cream-warm p-3 text-sm">
      <div className="grid grid-cols-2 gap-2">
        <input
          className={inputClass}
          placeholder="Anrede"
          value={a.anrede}
          onChange={(e) => setA({ ...a, anrede: e.target.value })}
        />
        <input
          className={inputClass}
          placeholder="Name"
          value={a.name}
          onChange={(e) => setA({ ...a, name: e.target.value })}
        />
        <input
          className={inputClass + ' col-span-2'}
          placeholder="Straße"
          value={a.strasse}
          onChange={(e) => setA({ ...a, strasse: e.target.value })}
        />
        <input
          className={inputClass}
          placeholder="PLZ"
          value={a.plz}
          onChange={(e) => setA({ ...a, plz: e.target.value })}
        />
        <input
          className={inputClass}
          placeholder="Ort"
          value={a.ort}
          onChange={(e) => setA({ ...a, ort: e.target.value })}
        />
        <input
          className={inputClass + ' col-span-2'}
          placeholder="Land"
          value={a.land}
          onChange={(e) => setA({ ...a, land: e.target.value })}
        />
      </div>
      {warnungen.length > 0 && (
        <ul className="space-y-0.5">
          {warnungen.map((w) => (
            <li key={w} className="flex items-start gap-1.5 text-xs text-coral">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}
      <label className="flex items-start gap-2 text-xs text-ink-soft">
        <input
          type="checkbox"
          checked={sync}
          onChange={(e) => setSync(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          Auch Stammadresse beim Kunden anpassen
          <span className="block text-ink-faint">
            (setzt nur die Tracking-Kopie, nicht BONUS selbst.)
          </span>
        </span>
      </label>
      <div className="flex gap-2">
        <Button variant="primary" onClick={speichern}>Speichern</Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
      </div>
    </div>
  );
}
